/*global BluePay*/
import { useQueryClient } from "@tanstack/react-query";
import SlideRoutes from "@thenumber/react-slide-routes";
import React, { useEffect, useRef } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router";
import uplist from "../adapters/uplist";
import welcome from "../assets/images/welcome.jpg";
import AuthHeader from "../components/order/AuthHeader";
import Footer from "../components/order/Footer";
// import { useAlertActions } from "../context/AlertContext";
import { useAuthentication } from "../context/AuthenticationContext";
import { useFormDetailsState } from "../context/FormDetailsContext";
import { isInternal } from "../helpers";
import { SIGN_UP_PATH_LIST } from "../helpers/constants";
import { useChargeType } from "../hooks";
import { AccountInfoPage, PaymentInfoPage, SignupPage } from "../pages";
import AuthRedirectRoute from "./hoc/AuthRedirectRoute";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useAlertActions } from "../context/AlertContext";
const testValues = process.env.NODE_ENV === "development" && {
  first_name: "Jacob",
  last_name: "Galati",
  email: `jacob+${crypto.randomUUID()}@realfoto.ca`,
  password: "asdfasdf",
  password_confirmation: "asdfasdf",
  phone_number: "250-388-6998",
  mls_id: crypto.randomUUID(),
  office_id: 61,
};

const createBluepayToken = ({
  cardNumber,
  expiryDate,
  cvc,
  first_name,
  last_name,
}) =>
  new Promise((resolve, reject) => {
    // Create a bluepay token, this is the same as validating the card and stores it
    // in Bluepay for future use.
    BluePay.createToken(
      {
        CARD_ACCOUNT: cardNumber.replace(RegExp(" ", "g"), ""),
        CARD_EXPIRE: expiryDate.replace(RegExp(" |-|\\/", "g"), ""),
        CARD_CVV2: cvc,
        NAME1: first_name,
        NAME2: last_name,
      },
      //Bluepay Callback
      (res) => {
        if (res.STATUS === "E") {
          reject({ message: res.MESSAGE });
          return;
        }
        return resolve(res.TRANS_ID);
      }
    );
  });

const SignupRoutes = () => {
  const methods = useForm({ defaultValues: testValues });
  const location = useLocation();
  const { setToken } = useAuthentication();
  const { charge_type } = useFormDetailsState();
  const { isAutoCharge } = useChargeType();
  const { action, goBack } = useHistory();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { setAlert } = useAlertActions();

  const ref = useRef();
  const queryClient = useQueryClient();
  useEffect(() => {
    const apiKey = process.env.REACT_APP_BLUEPAY_API_KEY;
    const accountId = process.env.REACT_APP_BLUEPAY_ACCOUNT_ID;
    BluePay.setCredentials(accountId, apiKey);
  }, []);

  const signupUser = async (form_data) => {
    const { cardNumber, expiryDate, cvc, cardType, ...rest } = form_data;
    const recaptcha_token = await executeRecaptcha("signup");
    const data = {
      ...rest,
      charge_type_id: charge_type,
      internal: isInternal(),
      recaptcha_token,
    };
    let bluepay_token = null;
    if (isAutoCharge) {
      bluepay_token = await createBluepayToken(form_data).catch((error) => {
        methods.setError("cardNumber", { message: error.message });
        return null;
      });
      if (!bluepay_token) return;
      data.credit_cards_attributes = [
        {
          account_number: cardNumber.slice(-4),
          card_expire: expiryDate,
          token: bluepay_token,
          card_type: cardType === "mastercard" ? "MC" : cardType,
        },
      ];
    }

    const user_data = await uplist
      .post("/users", data)
      .then(({ data }) => data)
      .catch(({ response, message }) => {
        if (response) {
          const { messages } = response?.data || {};
          if (messages) {
            Object.keys(messages).forEach((key) => {
              if (key === "recaptcha") {
                setAlert(messages[key]);
              } else {
                methods.setError(key, { message: messages[key] });
              }
            });
          }
          // setAlert(response.data?.full_messages);
          goBack();
          // if the error came from account creation (usually email is taken)
          // we want to redirect back to the Account Info page to display errors there.
          // redirect = true;
        } else {
          // setAlert(message);
        }
      });
    if (!user_data) return;
    const { token } = user_data;
    queryClient.invalidateQueries(["users"]);
    setToken(token);
  };

  return (
    <FormProvider {...methods} form={ref.current}>
      <Row className="mx-0">
        <Col md={6} xs={12} className="px-0">
          <img src={welcome} className="welcome-image" alt="welcome" />
        </Col>
        <Col md={6} xs={12} className="px-0">
          <AuthHeader className="home-header" />
          <Form
            onSubmit={methods.handleSubmit(signupUser)}
            ref={ref}
            noValidate
          >
            <SlideRoutes
              location={location}
              pathList={SIGN_UP_PATH_LIST}
              duration={action === "REPLACE" ? 0 : 500}
              animation="slide"
            >
              <AuthRedirectRoute path="/signup" component={SignupPage} />
              <AuthRedirectRoute
                path="/account-info"
                component={AccountInfoPage}
              />
              <AuthRedirectRoute
                path="/payment-info"
                component={PaymentInfoPage}
              />
            </SlideRoutes>
            <Footer
              list={SIGN_UP_PATH_LIST}
              form={ref.current}
              type="signup_form"
            />
          </Form>
        </Col>
      </Row>
    </FormProvider>
  );
};

export default SignupRoutes;
